import * as React from 'react';

import { idTypeLabel } from '@/domains/auth';
import { secAdvApi } from '../api/sec-adv-api';
import type {
  ISecAdvCatalogue,
  ISecAdvCatalogueAttribute,
  ISecAdvCatalogueValue
} from '../api/sec-adv-contracts';
import { refusalMessage } from '../refusal';
import { useAdminData } from './admin-page';
import { formatDay, formatMonth } from './days';

/**
 * How the SEC ADV register's values read on screen — an unanswered member, a dollar amount, a
 * yes or no, a code by the catalogue's label, and any value by the type the catalogue gives it —
 * one spelling for the firm list, the firm card and its filing, so a firm reads the same
 * wherever it is drawn.
 *
 * **Unanswered is said, never drawn as `0` or blank.** survey-api sends every member the feed can
 * leave unanswered as null (its register browse design §4.1): an ERA's RAUM, a phone-only main
 * address's office. A screen that printed `0` or nothing there would say the firm had answered.
 *
 * **A code is labelled by the catalogue, and falls back to itself.** The catalogue is survey-api's
 * labels for the feed's own names and codes, keyed by an element's dotted path and an attribute's
 * name (its design §7). A value it has no label for — or a catalogue that could not be read at
 * all — reads as the feed spelled it: the catalogue labels and never filters, and a type or a
 * code the SEC adds tomorrow must still show. Rejected: a table of labels in this app, which
 * would be a second copy of survey-api's transcription of Form ADV, and wrong on the day the two
 * part.
 *
 * Display, not validation: nothing here refuses, flags or compares a value.
 *
 * The catalogue's read for a screen (`useSecAdvCatalogue`) is here too, beside what it labels.
 */

/** "Not answered", muted — the one reading of an unanswered member. */
export function NotAnswered() {
  return <span className="text-muted-foreground">Not answered</span>;
}

/** Whole U.S. dollars, as survey-api sends every amount — at most 14 digits, which a JS number
 *  holds exactly. Both fraction bounds are set: a currency's default minimum is 2, and a maximum
 *  under the minimum is a `RangeError` in older engines. */
const DOLLARS = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
});

export function formatDollars(amount: number): string {
  return DOLLARS.format(amount);
}

const DOLLARS_AND_CENTS = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

/** An amount that may carry cents — a disclosure's judgement, a staff field's `money` — as
 *  whole dollars when it has none and with its cents when it has. */
export function formatMoney(amount: number): string {
  return Number.isInteger(amount) ? formatDollars(amount) : DOLLARS_AND_CENTS.format(amount);
}

const COUNT = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

/** A whole number, grouped — a count of employees, clients or accounts. */
export function formatCount(count: number): string {
  return COUNT.format(count);
}

export function yesNo(value: boolean): string {
  return value ? 'Yes' : 'No';
}

/** The identifier types that matched a participant to a firm, in words, joined — "Firm CRD
 *  number and Firm SEC number" — on the firm card and the person hub alike. */
export function matchedByText(matchedBy: readonly string[]): string {
  return matchedBy.map(idTypeLabel).join(' and ');
}

/** A JSON object, and not an array or null — the shape a record's element, a custodian and a
 *  catalogue are read as. */
export function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * The catalogue's one read this page load, shared with every other screen (`getCatalogue`), as
 * each screen that labels the register loads it: `data` null until it lands, and for good with
 * `error` set when it cannot be read — a screen then draws the feed's own names and codes, and
 * says why in `error`, survey-api's words (`refusalMessage`), so that a 403, a 500 and a dropped
 * connection read as three different things.
 */
export function useSecAdvCatalogue() {
  return useAdminData(async () => {
    try {
      return await secAdvApi.getCatalogue();
    } catch (error) {
      throw new Error(refusalMessage(error, 'The catalogue could not be read'));
    }
  });
}

/** Where the catalogue keeps a firm's type: `Rgstn/@FirmType`, a `code` whose values are the
 *  feed's two spellings, `Registered` and `ERA`. */
export const FIRM_TYPE = { path: 'Rgstn', name: 'FirmType' } as const;

/**
 * The catalogue's closed set for one `code` attribute, in the form's order — or `[]` when the
 * catalogue is not there (still being read, or unreadable), lacks the attribute, or gives it no
 * values.
 */
export function codeValues(
  catalogue: Nullable<ISecAdvCatalogue>,
  attribute: { path: string; name: string }
): ISecAdvCatalogueValue[] {
  const entry = catalogue?.elements
    .find((element) => element.path === attribute.path)
    ?.attributes.find((candidate) => candidate.name === attribute.name);
  return entry?.values ?? [];
}

/** A code's label among `values`, or null when the catalogue gives it none — the caller then
 *  shows the code itself. */
export function codeLabel(values: ISecAdvCatalogueValue[], code: string): Nullable<string> {
  return values.find((value) => value.value === code)?.label ?? null;
}

/** A code as its catalogue label, or as written where the catalogue gives it none. */
export function codeText(
  catalogue: Nullable<ISecAdvCatalogue>,
  attribute: { path: string; name: string },
  code: string
): string {
  return codeLabel(codeValues(catalogue, attribute), code) ?? code;
}

/** The code itself, small and in monospace — what a reader matches a screen to the form by. */
function RawCode({ code }: { code: string }) {
  return <code className="text-xs text-muted-foreground">{code}</code>;
}

/**
 * A code by its catalogue label, with the code beside it so a screen can be matched to the
 * filing — or the code alone where the catalogue gives it no label.
 */
export function LabelledCode({ code, values }: { code: string; values: ISecAdvCatalogueValue[] }) {
  const label = codeLabel(values, code);
  if (label === null) return <code>{code}</code>;
  return (
    <>
      {label} <RawCode code={code} />
    </>
  );
}

/** The value as the feed wrote it — a string as itself, anything else as JSON would spell it. */
export function asWritten(value: unknown): string {
  return typeof value === 'string' ? value : JSON.stringify(value);
}

/**
 * "Blank", muted — a value the feed wrote, and wrote as nothing. Not "Not answered": the feed says
 * that by leaving a value out, and this one is there. Drawn rather than left empty, since an
 * empty cell reads as a screen that failed to draw.
 */
export function Blank() {
  return <span className="text-muted-foreground">Blank</span>;
}

/** A value as the feed wrote it (`asWritten`), with an empty string read as `Blank`. */
export function Written({ value }: { value: unknown }) {
  return value === '' ? <Blank /> : asWritten(value);
}

/** A whole number from either of the feed's spellings — a record's digit string, a custodian's
 *  JSON number — or null when the value is neither. */
function wholeNumber(value: unknown): Nullable<number> {
  if (typeof value === 'number') return Number.isSafeInteger(value) ? value : null;
  if (typeof value === 'string' && /^\d+$/.test(value)) {
    const parsed = Number(value);
    return Number.isSafeInteger(parsed) ? parsed : null;
  }
  return null;
}

/**
 * One value, formatted by the type the catalogue gives its attribute (survey-api's design §7):
 * `yes_no` as Yes / No from a record's `Y` / `N` or a custodian's JSON boolean, `amount_usd` as
 * dollars and `integer` grouped, from a digit string or a JSON number, `date` and `year_month`
 * as the day or month they name, a `code` by its label beside the code, and `text` as written.
 *
 * **What does not read as its type is shown as the feed wrote it** — an older filing's range
 * such as `11-25` in an integer, an attribute the catalogue does not list, a type this build has
 * no words for. Display, not validation: nothing is refused, flagged or corrected, and the raw
 * value is always what a fallback shows. An absent or null value is not answered, and an empty
 * one is `Blank`, whatever its type.
 */
export function CatalogueValue({
  attribute,
  value
}: {
  attribute: Nullable<ISecAdvCatalogueAttribute>;
  value: unknown;
}): React.ReactNode {
  if (value === undefined || value === null) return <NotAnswered />;
  if (value === '') return <Blank />;
  const written = asWritten(value);
  switch (attribute?.valueType) {
    case 'yes_no':
      if (value === true || value === 'Y') return 'Yes';
      if (value === false || value === 'N') return 'No';
      return written;
    case 'amount_usd': {
      const amount = wholeNumber(value);
      return amount === null ? written : formatDollars(amount);
    }
    case 'integer': {
      const count = wholeNumber(value);
      return count === null ? written : formatCount(count);
    }
    case 'date':
      return typeof value === 'string' ? formatDay(value) : written;
    case 'year_month': {
      if (typeof value !== 'string') return written;
      const month = formatMonth(`${value}-01`);
      return month === `${value}-01` ? written : month;
    }
    case 'code':
      return typeof value === 'string' ? (
        <LabelledCode code={value} values={attribute.values ?? []} />
      ) : (
        written
      );
    default:
      return written;
  }
}
